import type {HTMLAttributes} from 'react'

import {useBoardConfig, useBoardDataContext} from '../provider/contexts'
import type {BoardRow, RowKey} from '../types'

export type UseBoardRowResult = {
  /** The row at this key, or `undefined` when the board holds no such row. */
  row: BoardRow | undefined
  rowProps: HTMLAttributes<HTMLElement>
}

/**
 * One swimlane's frame and its header. A board with no swimlane has exactly one row, so
 * this hook runs for every board and nothing anywhere asks whether the board is
 * swimlaned. Like a column, a row is not a drop target.
 *
 * **The swimlane's count is in its name**, because a reader who arrives inside the
 * swimlane rather than reading down to its header hears the group and never the number
 * beside it. That is the same reason a cell states its own count, and the header still
 * draws the number for everybody who can see it.
 *
 * A swimlane's name is not a place the board's focus lands. It is not interactive, the
 * cell a reader arrives in is already named after the swimlane it is in, and a stop there
 * would be one more position a caller has to draw or lose the arrow keys to.
 */
export function useBoardRow(rowKey: RowKey): UseBoardRowResult {
  const {rowsByKey} = useBoardDataContext()
  const {labels} = useBoardConfig()
  const row = rowsByKey.get(rowKey)

  return {
    row,
    // A key with no row behind it gets no role at all. An unnamed group is dead
    // semantics, and it would silently defeat the locators in `@github-ui/board/test-utils`.
    rowProps: row ? {role: 'group', 'aria-label': labels.row({rowLabel: row.label, totalCount: row.totalCount})} : {},
  }
}
